import React, { useContext, useState } from "react";
import { toast } from "react-toastify";
import CustomInput from "../../../components/CustomInput";
import DragDrop from "../../../components/DragDrop";
import Modal from "../../../components/Modal";
import { AuthContext } from "../../../../../contexts/AuthContext";
import { firebaseService } from "../../../../../services/api/firebaseService";

const ContractPopup = ({
  mode = "create", // "create" | "edit"
  document = null,
  closePopup,
  handleAddDocument,
  handleEditDocument,
}) => {
  const { token } = useContext(AuthContext);
  const isEdit = mode === "edit";

  const [formData, setFormData] = useState({
    name: document?.name || "",
    pdfFile: null,
  });
  const [existingPdfUrl, setExistingPdfUrl] = useState(document?.pdfUrl || "");
  const [isEditingPdf, setIsEditingPdf] = useState(!document?.pdfUrl);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const beautifyFileName = (originalName) => {
    return originalName.replace(/%20/g, " ").replace(/\s+/g, "_");
  };

  const getFileNameFromUrl = (url = "") => {
    if (!url || typeof url !== "string") return "";
    try {
      const cleanUrl = url.split("?")[0];
      const name = cleanUrl.split("/").pop();
      return decodeURIComponent(name || "");
    } catch {
      return "";
    }
  };

  const isFirebaseUrl = (val) =>
    typeof val === "string" &&
    val.startsWith("https://storage.googleapis.com/");

  const onSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Vui lòng nhập tên hợp đồng.");
      return;
    }

    if (!isEdit && !formData.pdfFile) {
      toast.error("Vui lòng tải lên file hợp đồng.");
      return;
    }

    if (isEdit && isEditingPdf && !formData.pdfFile) {
      toast.error("Vui lòng chọn file PDF mới.");
      return;
    }

    // Validate PDF file if provided
    if (
      formData.pdfFile &&
      !(
        formData.pdfFile.type === "application/pdf" ||
        formData.pdfFile.name.toLowerCase().endsWith(".pdf")
      )
    ) {
      toast.error("Chỉ chấp nhận file PDF.");
      return;
    }

    setIsSubmitting(true);

    try {
      let pdfUrl = existingPdfUrl;

      // Upload new PDF file if selected
      if (formData.pdfFile) {
        const uploadResult = await firebaseService.upload_file(
          token,
          formData.pdfFile,
        );

        if (!uploadResult?.url) {
          toast.error("Upload file PDF thất bại.");
          setIsSubmitting(false);
          return;
        }

        pdfUrl = uploadResult.url;

        // Delete old file if editing and old file is from Firebase
        if (isEdit && isFirebaseUrl(existingPdfUrl)) {
          try {
            await firebaseService.delete_file(token, existingPdfUrl);
          } catch (err) {
            console.warn("Failed to delete old PDF:", err);
          }
        }
      }

      const documentData = {
        name: formData.name.trim(),
        pdfFile: pdfUrl,
      };

      if (isEdit) {
        await handleEditDocument(document._id, documentData);
        toast.success("Cập nhật hợp đồng thành công!");
      } else {
        await handleAddDocument(documentData);
        toast.success("Thêm hợp đồng thành công!");
      }

      closePopup();
    } catch (error) {
      console.error("Error saving document:", error);
      toast.error(
        isEdit
          ? "Lỗi khi cập nhật hợp đồng."
          : "Lỗi khi tải lên file hợp đồng.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelClick = () => {
    setShowCancelConfirm(true);
  };

  const handleCancelConfirm = () => {
    setShowCancelConfirm(false);
    closePopup();
  };

  return (
    <>
      <Modal
        isOpen={true}
        onClose={closePopup}
        title={isEdit ? "Chỉnh sửa hợp đồng" : "Thêm hợp đồng mới"}
        maxWidth="50%"
        maxHeight="calc(100vh - 100px)"
        primaryButtonText={
          isSubmitting
            ? isEdit
              ? "Đang cập nhật..."
              : "Đang tải lên..."
            : isEdit
              ? "Lưu"
              : "Thêm"
        }
        primaryButtonOnClick={onSubmit}
        cancelButtonText="Hủy"
        secondaryButtonOnClick={handleCancelClick}
      >
        <div className="px-6 py-4">
          <div className="flex flex-col gap-1 py-2">
            <strong className="text-gray-700">Tên hợp đồng:</strong>
            <CustomInput
              placeholder="Nhập tên hợp đồng"
              value={formData.name}
              required={true}
              onChange={(value) => setFormData({ ...formData, name: value })}
              disabled={isSubmitting}
            />
          </div>

          <div className="flex flex-col border-b border-[#A2A1A810] gap-1 py-2">
            <strong className="text-gray-700">File hợp đồng:</strong>

            {isEdit && existingPdfUrl && !isEditingPdf ? (
              <div className="flex items-center justify-between rounded-lg border py-3 px-4 bg-gray-50">
                <div className="flex gap-2 items-center w-3/4">
                  <img
                    src="/images/document.png"
                    alt="document"
                    className="w-5 h-5"
                  />
                  <a
                    href={existingPdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline truncate"
                  >
                    {beautifyFileName(getFileNameFromUrl(existingPdfUrl))}
                  </a>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditingPdf(true);
                    setFormData((prev) => ({ ...prev, pdfFile: null }));
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                  title="Thay file"
                  disabled={isSubmitting}
                >
                  <img
                    src="/icons/x-close.svg"
                    alt="Thay file"
                    className="h-4 w-4"
                  />
                </button>
              </div>
            ) : (
              <DragDrop
                label={
                  formData.pdfFile
                    ? formData.pdfFile.name
                    : "Kéo thả file PDF hợp đồng tại đây"
                }
                acceptedFormats={["pdf"]}
                maxFiles={1}
                onFileChange={(file) => {
                  const selectedFile = file?.[0] || null;
                  setFormData({ ...formData, pdfFile: selectedFile });
                  if (selectedFile && isEdit) setIsEditingPdf(true);
                }}
                disabled={isSubmitting}
              />
            )}
          </div>
        </div>
      </Modal>

      {/* Cancel Confirmation Modal */}
      <Modal
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        title="Xác nhận hủy"
        maxWidth="400px"
        primaryButtonText="Đồng ý"
        primaryButtonOnClick={handleCancelConfirm}
        cancelButtonText="Quay lại"
      >
        <div className="px-6 py-4">
          <p className="text-gray-700">
            Bạn có chắc chắn muốn hủy thao tác không?
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Các thay đổi chưa lưu sẽ bị mất.
          </p>
        </div>
      </Modal>
    </>
  );
};

export default ContractPopup;
